import type {
  WarmupGenerateResult,
  WarmupSubmitResult,
  WarmupFinishResult,
  WarmupQuestion,
  WarmupWrongQuestion,
  WarmupAccessStatus,
} from '@jeraisonne/db';
import { publishProgress } from '@/lib/progress-sync';
import { supabase } from '@/lib/supabase';

/**
 * Client des RPC Warmup (mode d'entraînement gratuit).
 *
 * Le Warmup passe par 3 RPC Postgres SECURITY DEFINER (cf. migration 0062) :
 *   generate_warmup_for_student → crée la session + renvoie 10 questions
 *   submit_warmup_answer        → enregistre une réponse
 *   finish_warmup               → finalise, renvoie le score et les erreurs
 */

export type WarmupErrorCode =
  | 'UNAUTHENTICATED'
  | 'PROFILE_NOT_FOUND'
  | 'NOT_A_STUDENT'
  | 'NO_QUESTIONS_AVAILABLE'
  | 'ATTEMPT_NOT_FOUND'
  | 'ATTEMPT_ALREADY_FINISHED'
  | 'STEP_INVALID'
  | 'CHOICE_INVALID'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export class WarmupError extends Error {
  constructor(
    public readonly code: WarmupErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'WarmupError';
  }
}

export function warmupErrorMessage(code: WarmupErrorCode): string {
  switch (code) {
    case 'NO_QUESTIONS_AVAILABLE':
      return "Pas encore de questions d'échauffement disponibles. Reviens bientôt !";
    case 'NOT_A_STUDENT':
      return 'Le Warmup est réservé aux élèves.';
    case 'ATTEMPT_NOT_FOUND':
      return 'Session de Warmup introuvable. Relance un échauffement.';
    case 'ATTEMPT_ALREADY_FINISHED':
      return 'Cette session de Warmup est déjà terminée.';
    case 'UNAUTHENTICATED':
      return 'Tu dois être connecté pour faire un Warmup.';
    case 'NETWORK_ERROR':
      return 'Erreur réseau. Vérifie ta connexion et réessaie.';
    default:
      return 'Une erreur est survenue. Réessaie.';
  }
}

function hasError(data: unknown): data is { error: string } {
  return !!data && typeof data === 'object' && 'error' in data;
}

export type WarmupSession = {
  attemptId: string;
  accessStatus: WarmupAccessStatus;
  questions: WarmupQuestion[];
};

/** Crée une session Warmup et récupère les questions. */
export async function generateWarmup(questionCount = 10): Promise<WarmupSession> {
  const { data, error } = await supabase.rpc('generate_warmup_for_student', {
    p_question_count: questionCount,
  });
  if (error) throw new WarmupError('NETWORK_ERROR', error.message);

  const result = data as WarmupGenerateResult;
  if (hasError(result)) {
    throw new WarmupError((result.error as WarmupErrorCode) ?? 'UNKNOWN', result.error);
  }
  return {
    attemptId: result.attempt_id,
    accessStatus: result.access_status,
    questions: result.questions,
  };
}

export type WarmupAnswerResult = {
  wasCorrect: boolean;
  explanationMd: string;
  feedbackMd: string | null;
};

/** Enregistre la réponse de l'élève à une question de Warmup. */
export async function submitWarmupAnswer(params: {
  attemptId: string;
  stepId: string;
  choiceId: string;
  timeMs: number;
}): Promise<WarmupAnswerResult> {
  const { data, error } = await supabase.rpc('submit_warmup_answer', {
    p_attempt_id: params.attemptId,
    p_step_id: params.stepId,
    p_choice_id: params.choiceId,
    p_time_ms: params.timeMs,
  });
  if (error) throw new WarmupError('NETWORK_ERROR', error.message);

  const result = data as WarmupSubmitResult;
  if (hasError(result)) {
    throw new WarmupError((result.error as WarmupErrorCode) ?? 'UNKNOWN', result.error);
  }
  return {
    wasCorrect: result.was_correct,
    explanationMd: result.explanation_md,
    feedbackMd: result.feedback_md,
  };
}

export type WarmupSummary = {
  score: number;
  total: number;
  correct: number;
  xpAwarded: number;
  wrongQuestions: WarmupWrongQuestion[];
};

/** Finalise une session Warmup et récupère le score + les erreurs. */
export async function finishWarmup(attemptId: string): Promise<WarmupSummary> {
  const { data, error } = await supabase.rpc('finish_warmup', { p_attempt_id: attemptId });
  if (error) throw new WarmupError('NETWORK_ERROR', error.message);

  const result = data as WarmupFinishResult;
  if (hasError(result)) {
    throw new WarmupError((result.error as WarmupErrorCode) ?? 'UNKNOWN', result.error);
  }
  publishProgress('session-finished');
  return {
    score: result.score,
    total: result.total,
    correct: result.correct,
    xpAwarded: result.xp_awarded,
    wrongQuestions: result.wrong_questions,
  };
}
